import { useEffect, useState } from "react";
import { es } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Campo de fecha siempre en DD/MM/AAAA (el <input type="date"> nativo usa el idioma del navegador y
// en Chrome en inglés muestra MM/DD/AAAA). Se escribe o se elige en el calendario.
// Mismo contrato que el input nativo: `value` = "AAAA-MM-DD" (o "") y onChange recibe { target: { value } },
// así los formularios existentes no cambian.
const aTexto = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

const aIso = (texto) => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((texto || "").trim());
  if (!m) return null;
  const [d, mes, a] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const f = new Date(a, mes - 1, d);
  if (f.getFullYear() !== a || f.getMonth() !== mes - 1 || f.getDate() !== d) return null; // 31/02 no existe
  return `${a}-${String(mes).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

// Inserta las diagonales mientras se escribe: "25092026" -> "25/09/2026".
const enmascarar = (t) => {
  const dig = t.replace(/\D/g, "").slice(0, 8);
  return [dig.slice(0, 2), dig.slice(2, 4), dig.slice(4, 8)].filter(Boolean).join("/");
};

export default function FechaInput({ value, onChange, disabled, className, placeholder = "DD/MM/AAAA", ...rest }) {
  const [texto, setTexto] = useState(aTexto(value));
  const [abierto, setAbierto] = useState(false);
  useEffect(() => { setTexto(aTexto(value)); }, [value]);

  const emitir = (iso) => onChange?.({ target: { value: iso } });
  const invalido = texto.length === 10 && !aIso(texto);
  const seleccion = value ? new Date(`${value.slice(0, 10)}T12:00:00`) : undefined;

  return (
    <div className={cn("relative flex items-center", className)}>
      <input
        type="text"
        inputMode="numeric"
        value={texto}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          const t = enmascarar(e.target.value);
          setTexto(t);
          if (t === "") emitir("");
          const iso = aIso(t);
          if (iso) emitir(iso);
        }}
        onBlur={() => { if (texto && !aIso(texto)) setTexto(aTexto(value)); }}
        className={cn(
          "flex h-9 w-full rounded-md border bg-transparent px-3 py-1 pr-9 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          invalido ? "border-red-400" : "border-input"
        )}
        {...rest}
      />
      <Popover open={abierto} onOpenChange={setAbierto}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} className="absolute right-2 text-slate-400 hover:text-slate-700 disabled:opacity-40" aria-label="Elegir fecha">
            <CalendarDays size={16} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            locale={es}
            weekStartsOn={1}
            selected={seleccion}
            defaultMonth={seleccion}
            onSelect={(d) => {
              if (!d) return;
              const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              setTexto(aTexto(iso));
              emitir(iso);
              setAbierto(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
